import React, { useEffect, useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  getCompanyInfo,
  getUkgConfig,
  submitUkgConfig,
} from "actions/employerActions";
import { employerConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";
import UkgConfigForm from "../dashboard/UkgConfigForm";
import IconSpinner from "components/IconSpinner";
import type { RootState } from "types/state";

const errorSelector = createErrorSelector(employerConstants.SUBMIT_UKG_CONFIG);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
});

const mapDispatchToProps = {
  submitUkgConfig,
  getCompanyInfo,
  getUkgConfig,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface UkgSubmitValues {
  clientId: string;
  secret: string;
  companyCode: string;
}

const UkgCredentials = (props: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ukgConfig, setUkgConfig] = useState<Record<string, unknown> | null>(
    null
  );

  useEffect(() => {
    const fetchConfig = async () => {
      const result = await props.getUkgConfig(
        props.client as ApolloClient<object>
      );
      if (result.success) {
        // The dispatched action union widens `type` to string, so TS can't
        // narrow to the success branch here; the runtime guard above
        // guarantees `data` is present. Cast preserves the original behavior.
        const successResult = result as {
          type: string;
          data: Record<string, unknown> | null;
        };
        setUkgConfig(successResult.data);
      }
      setIsLoading(false);
    };
    void fetchConfig();
  }, []);

  const handleSubmit = async ({
    clientId,
    secret,
    companyCode,
  }: UkgSubmitValues) => {
    setIsSubmitting(true);

    const result = await props.submitUkgConfig(
      props.client as ApolloClient<object>,
      {
        clientId,
        secret,
        companyCode,
      }
    );

    if (result.success) {
      toast.success("UKG credentials updated successfully.");
      void props.getCompanyInfo(props.client as ApolloClient<object>);
    }

    setIsSubmitting(false);
  };

  if (isLoading) {
    return <IconSpinner centered />;
  }

  return (
    <Card>
      <Card.Header>
        <h5>UKG OAuth Credentials</h5>
      </Card.Header>
      <Card.Body>
        <p className="mb-4">
          Enter your UKG OAuth credentials to connect your payroll system.
        </p>
        <UkgConfigForm
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={props.error}
          // @ts-expect-error ukgConfig is Record<string, unknown>, so .clientId is unknown; runtime value is the string from getUkgConfig — preserved from JS
          clientId={ukgConfig?.clientId}
          // @ts-expect-error ukgConfig is Record<string, unknown>, so .companyCode is unknown; runtime value is the string from getUkgConfig — preserved from JS
          companyCode={ukgConfig?.companyCode}
        />
      </Card.Body>
    </Card>
  );
};

export default connector(withApollo<Props>(UkgCredentials));
